import L from "leaflet";
import { Marker, useMap } from "react-leaflet";
import * as ReactDOMServer from "react-dom/server";
import { useTheme } from "@mui/material";
import { sanitizeHtml, escapeHtml } from "../../../../../../utils/sanitizeHtml";

// Camada que renderiza um elemento do tipo 3 (caixa de texto/HTML)

const MAX_WIDTH = "clamp(150px, 10vw, 400px)";
const MAX_HEIGHT = "clamp(150px, 10vh, 400px)";
const TEXTBOX_PANE = "easymaps-textbox-pane";
const TEXTBOX_PANE_ZINDEX = 350;
const TEXTBOX_EDITABLE_PANE = "easymaps-textbox-editable-pane";
const TEXTBOX_EDITABLE_PANE_ZINDEX = 401;

function ensurePane(map, name, zIndex) {
  if (!map || map.getPane(name)) return;
  map.createPane(name);
  const pane = map.getPane(name);
  if (pane) pane.style.zIndex = String(zIndex);
}

function ensureTextBoxStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById("easymaps-textbox-style")) return;
  const style = document.createElement("style");
  style.id = "easymaps-textbox-style";
  style.textContent = `
    /* Keep the wrapper expanding to match its inner content tightly */
    .easymaps-textbox {
      width: auto !important;
      height: auto !important;
      background: transparent !important;
      border: none !important;
      position: absolute;
    }
    .easymaps-textbox-content {
      max-width: ${MAX_WIDTH};
      max-height: ${MAX_HEIGHT};
      overflow: hidden;
      box-sizing: border-box;
    }
    .easymaps-textbox-content * {
      max-width: 100% !important;
      max-height: ${MAX_HEIGHT} !important;
      box-sizing: border-box !important;
    }
    .easymaps-textbox-content img,
    .easymaps-textbox-content video,
    .easymaps-textbox-content svg,
    .easymaps-textbox-content table {
      width: auto !important;
      height: auto !important;
      object-fit: contain;
    }
  `;
  document.head.appendChild(style);
}

export default function TextBoxLayer({
  element,
  editable,
  selected,
  onNodeClick,
  handleUpdatePositionElement,
}) {
  const theme = useTheme();
  const map = useMap();
  ensureTextBoxStyles();
  ensurePane(map, TEXTBOX_PANE, TEXTBOX_PANE_ZINDEX);
  ensurePane(map, TEXTBOX_EDITABLE_PANE, TEXTBOX_EDITABLE_PANE_ZINDEX);

  const box = element?.elementConfig?.config?.box || {
    mode: "text",
    content: "",
  };
  const lat = parseFloat(element?.elementConfig?.config?.coordenadas?.lat);
  const long = parseFloat(element?.elementConfig?.config?.coordenadas?.long);
  if (!Number.isFinite(lat) || !Number.isFinite(long)) return null;

  const inner =
    box.mode === "html"
      ? sanitizeHtml(box.content || "")
      : escapeHtml(box.content || "").replace(/\n/g, "<br/>");

  // Fonte configurável (apenas no modo texto); usa o padrão quando ausente.
  const parsedFontSize = Number(box.fontSize);
  const fontSize =
    box.mode !== "html" && Number.isFinite(parsedFontSize) && parsedFontSize > 0
      ? parsedFontSize
      : 13;

  const html = ReactDOMServer.renderToString(
    <div
      className="easymaps-textbox-content"
      style={{
        width: "max-content",
        maxWidth: MAX_WIDTH,
        minWidth: 24,
        maxHeight: MAX_HEIGHT,
        padding: "2px 4px",
        borderRadius: 1,
        background: box.backgroundColor || theme.palette.background.paper,
        color: box.textColor || theme.palette.text.primary,
        border: `1px solid ${
          selected ? theme.palette.primary.main : theme.palette.divider
        }`,
        boxShadow: selected
          ? `0 0 8px ${theme.palette.primary.main}`
          : "0 1px 4px rgba(0,0,0,.3)",
        fontSize,
        lineHeight: 1.4,
        cursor: editable ? "move" : "default",
        pointerEvents: "auto",
        overflow: "hidden",
        overflowWrap: "break-word",
        boxSizing: "border-box",
      }}
      dangerouslySetInnerHTML={{ __html: inner }}
    />,
  );

  const icon = L.divIcon({
    html,
    className: "easymaps-textbox",
    iconSize: null,
  });
  icon.createIcon = function (oldIcon) {
    const div = L.DivIcon.prototype.createIcon.call(this, oldIcon);

    queueMicrotask(() => {
      const child = div.querySelector(".easymaps-textbox-content");
      if (child) {
        const width = child.offsetWidth;
        const height = child.offsetHeight;
        div.style.marginLeft = `${-width / 2}px`;
        div.style.marginTop = `${-height / 2}px`;
      }
    });

    return div;
  };

  const pane = editable ? TEXTBOX_EDITABLE_PANE : TEXTBOX_PANE;

  return (
    <Marker
      key={pane}
      draggable={editable && selected}
      position={[lat, long]}
      icon={icon}
      pane={pane}
      eventHandlers={{
        click: (e) => {
          L.DomEvent.stopPropagation(e);
          onNodeClick(e, element);
        },
        dragend: (e) => {
          handleUpdatePositionElement(element, e.target._latlng);
        },
      }}
    />
  );
}
